<template>
  <div class="metric">
    <span class="lbl">
      <span v-if="tip" class="tip" :data-tip="tip">{{ label }}</span>
      <template v-else>{{ label }}</template>
    </span>
    <span :class="['val', state]">
      <CountUp v-if="numericValue !== null" :to="numericValue" :suffix="suffix" />
      <template v-else>{{ value }}</template>
    </span>
    <span class="bar" v-if="bar !== undefined">
      <i ref="barInner" :class="state" :style="barStyle" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import CountUp from "./CountUp.vue";

const props = withDefaults(
  defineProps<{
    label: string;
    /** Display value: number, "12%", "Yes", "No", "—". */
    value: string | number;
    /** Percent for the progress bar (0–100). */
    bar?: number;
    state?: "bad" | "warn" | "ok" | "neutral";
    tip?: string;
  }>(),
  { state: "neutral" },
);

const numericValue = computed<number | null>(() => {
  if (typeof props.value === "number") return props.value;
  const m = props.value.match(/^(\d+(?:\.\d+)?)(%?)$/);
  return m ? Number(m[1]) : null;
});
const suffix = computed(() => (typeof props.value === "string" && props.value.endsWith("%") ? "%" : ""));

const barInner = ref<HTMLElement | null>(null);
const barStyle = ref<Record<string, string>>({ width: "0%" });
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (props.bar === undefined) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!barInner.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const w = Math.max(2, Math.min(100, props.bar ?? 0));
        if (reduced) {
          barStyle.value = { width: `${w}%`, transition: "none" };
        } else {
          requestAnimationFrame(() => { barStyle.value = { width: `${w}%` }; });
        }
        observer?.disconnect();
        observer = null;
        break;
      }
    },
    { threshold: 0.2 },
  );
  observer.observe(barInner.value);
});

onUnmounted(() => { observer?.disconnect(); observer = null; });
</script>

<style scoped>
.metric { display: grid; gap: 4px; min-width: 0; }
.lbl {
  font-family: "JetBrains Mono", monospace;
  font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--muted-2);
}
.val {
  font-size: 20px; font-weight: 500; letter-spacing: -0.025em;
  color: var(--ink); line-height: 1;
  font-variant-numeric: tabular-nums;
}
.val.bad  { color: var(--red); }
.val.warn { color: var(--amber-ink); }
.val.ok   { color: var(--green-ink); }

.bar {
  height: 3px; background: var(--line-2); border-radius: 2px;
  overflow: hidden; margin-top: 2px;
}
.bar i {
  display: block; height: 100%; width: 0;
  background: var(--ink-2);
  transition: width var(--speed-bar) var(--ease-reveal);
}
.bar i.bad  { background: var(--red); }
.bar i.warn { background: var(--amber); }
.bar i.ok   { background: var(--green); }
</style>
